import { Spin } from 'lego-on-react';
import React, { useCallback, useEffect, useState } from 'react';

import i18n from '@yandex-int/i18n';
import { getErrorMessage } from 'common/utils/errorsHandler';
import * as keyset from 'i18n/AccountLinking';
import stubTrue from 'lodash/stubTrue';
import block from 'propmods';

import { attachOauth, deattachOauth, getOAuthApp } from '../../api';
import { useSnapshotRefresher } from '../../hooks/useSnapshotRefresher';
import { UpdateOAuthAppSettings } from '../../model/oauth';
import { Channel, Skill } from '../../model/skill';
import { ErrorBoundary } from '../ErrorBoundary/ErrorBoundary';
import { Header } from '../Form/Form';
import OAuthAppForm from '../OAuthSettings/OAuthAppForm';

import './AccountLinkingSettings.scss';

const t = i18n(keyset);

const b = block('AccountLinkingSettings');

interface Props {
    skill: Skill;
    disabled: boolean;
}

const emptyOauthApp = {
    name: '',
    clientId: '',
    clientSecret: '',
    authorizationUrl: '',
    tokenUrl: '',
    refreshTokenUrl: '',
    scope: '',
    yandexClientId: '',
};

function AccountLinkingSettings(props: Props) {
    const { skill, disabled } = props;
    const refreshSnapshot = useSnapshotRefresher();
    const isAvailable =
        (skill.channel === Channel.AliceSkill && __appConfig.user.featureFlags.allowSetAccountLinkingForAliceSkills) ||
        skill.channel === Channel.SmartHome;

    const [oauthAppId, setOauthAppId] = useState(skill.draft.oauthAppId);

    const [isLoading, setIsLoading] = useState(Boolean(oauthAppId));
    const [oauthApp, setOauthApp] = useState<UpdateOAuthAppSettings>({
        ...emptyOauthApp,
    });
    const [saveError, setSaveError] = useState('');

    useEffect(() => {
        if (isAvailable && oauthAppId) {
            void fetchOAuthApp(oauthAppId);
        }
    }, [isAvailable, oauthAppId]);

    const onChange = useCallback(
        async (id: string | null) => {
            try {
                if (!oauthAppId && id) {
                    await attachOauth(skill.id, id);
                    setOauthAppId(id);
                }
                if (oauthAppId && !id) {
                    await deattachOauth(skill.id);
                    setOauthAppId('');
                }
                await refreshSnapshot();
                if (id) {
                    fetchOAuthApp(id);
                }
            } catch (error) {
                const errorMessage = getErrorMessage(error);
                setSaveError(errorMessage ?? t('error-occured'));
            }
        },
        [oauthAppId, refreshSnapshot, skill.id],
    );

    if (!isAvailable) {
        return null;
    }

    const fetchOAuthApp = async (id: string) => {
        try {
            setIsLoading(true);
            const app = await getOAuthApp(id);
            setOauthApp({
                ...app,
                clientSecret: '',
                id: app.id,
            });
        } catch (e) {
            console.error(e);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <ErrorBoundary withHomepageLink>
            <Header title={t('account-linking')} />
            <div {...b()}>
                {isLoading ? (
                    <div {...b('lazy-spinner')}>
                        <Spin size="m" progress />
                    </div>
                ) : (
                    <OAuthAppForm
                        key={oauthApp.id}
                        hasChanges={false}
                        registerChanges={stubTrue}
                        settings={oauthApp}
                        onUpdate={onChange}
                        name={skill.name || skill.draft.name}
                        disabled={disabled}
                    />
                )}
                {saveError && <div {...b('error')}>{saveError}</div>}
            </div>
        </ErrorBoundary>
    );
}

export default AccountLinkingSettings;
